export interface DecodedClaims {
  sub: string;
  email?: string;
  /** MyHours-ADR-0007/D-35: Standard-OIDC-Claims, von Cognito automatisch mitgeliefert sobald gesetzt. */
  givenName?: string;
  familyName?: string;
  groups: string[];
  divisionIds: string[];
}

/**
 * `atob()` decodes base64 into a "binary string" (one JS char per byte, Latin-1), not proper
 * Unicode -- any multi-byte UTF-8 sequence (e.g. "Ü" in a given_name/family_name claim, MyHours-ADR-0007)
 * comes out as mojibake unless the byte string is re-decoded as UTF-8 explicitly.
 */
function base64UrlToUtf8(base64Url: string): string {
  const binary = atob(base64Url.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

/** Decodes (but does NOT verify) an ID token's payload, purely for UI display/routing decisions. */
export function decodeIdToken(idToken: string): DecodedClaims {
  const payload = idToken.split('.')[1];
  if (!payload) throw new Error('Malformed ID token');
  const json = JSON.parse(base64UrlToUtf8(payload)) as Record<string, unknown>;
  const groupsRaw = json['cognito:groups'];
  const groups = Array.isArray(groupsRaw) ? groupsRaw.map(String) : [];
  const divisionIdsRaw = json['custom:divisionIds'];
  const divisionIds =
    typeof divisionIdsRaw === 'string' && divisionIdsRaw.length > 0
      ? divisionIdsRaw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  return {
    sub: String(json.sub ?? ''),
    email: typeof json.email === 'string' ? json.email : undefined,
    givenName: typeof json.given_name === 'string' ? json.given_name : undefined,
    familyName: typeof json.family_name === 'string' ? json.family_name : undefined,
    groups,
    divisionIds,
  };
}
